import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  HiOutlineArrowPath,
  HiOutlineInformationCircle,
  HiOutlinePencilSquare,
  HiOutlineShare,
  HiOutlineMapPin,
  HiOutlineCheckCircle,
  HiOutlineChatBubbleLeft,
  HiOutlineXMark,
  HiOutlinePuzzlePiece,
  HiOutlineMagnifyingGlass,
  HiOutlineClipboardDocumentCheck,
  HiOutlineTrash,
} from "react-icons/hi2";


/* --- helpers --- */
const statusBadge = (status) => {
  const map = {
    Pendiente: "badge-yellow",
    Activo: "badge-green",
    Apartado: "badge-blue",
    Extendido: "badge-blue",
    Finalizado: "badge-gray",
    Rechazado: "badge-red",
  };
  return map[status] || "badge-gray";
};

const toLocalDateFromUTCDateOnly = (iso) => {
  if (!iso) return null;
  const [datePart] = iso.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatFechaCortaMX = (iso) => {
  const d = toLocalDateFromUTCDateOnly(iso);
  return d?.toLocaleDateString("es-MX", { weekday: "short", day: "2-digit", month: "short" }) || "\u2014";
};

const formatFecha = (fechaISO) => {
  const fechaSinHora = fechaISO.split("T")[0];
  const [anio, mes, dia] = fechaSinHora.split("-");
  const meses = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  return `${parseInt(dia, 10)} de ${meses[parseInt(mes, 10) - 1]}, ${anio}`;
};

const formatHora = (hora) => {
  const [hh, mm] = hora.split(":");
  const horaInt = parseInt(hh, 10);
  const periodo = horaInt >= 12 ? "PM" : "AM";
  const hora12 = horaInt % 12 || 12;
  return `${hora12}:${mm} ${periodo}`;
};

const getPrimerDiaItinerario = (itinerario = []) => {
  if (!Array.isArray(itinerario) || itinerario.length === 0) return null;
  const sorted = [...itinerario].sort((a, b) => toLocalDateFromUTCDateOnly(a.fecha) - toLocalDateFromUTCDateOnly(b.fecha));
  return sorted[0];
};

/* --- Component --- */
const ActivityPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [reservas, setReservas] = useState([]);
  const [reservasActivas, setReservasActivas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPendientes, setLoadingPendientes] = useState(true);
  const [loadingActivas, setLoadingActivas] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReserva, setSelectedReserva] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openCompartir, setOpenCompartir] = useState(false);
  const [selectedReservaCompartir, setSelectedReservaCompartir] = useState(null);
  const [shareWith, setShareWith] = useState("");
  const [reservasCompartidas, setReservasCompartidas] = useState([]);
  const [loadingCompartidas, setLoadingCompartidas] = useState(true);
  const [errorCompartidas, setErrorCompartidas] = useState(null);
  const [searchTicket, setSearchTicket] = useState("");
  const [tecnicos, setTecnicos] = useState([]);
  const [agentes, setAgente] = useState([]);

  /* --- Fetchers --- */
  const fetchReservas = async () => {
    setLoading(true);
    setError(null);
    fetchReservasActivas();
    try {
      const pendientes = await axios.get(`https://desarrollotecnologicoar.com/api5/reservas_agente/${user.email}/Pendiente`);
      const apartadas = await axios.get(`https://desarrollotecnologicoar.com/api5/reservas_agente/${user.email}/Apartado`);
      const todas = [...pendientes.data, ...apartadas.data];
      const ordenadas = todas.sort((a, b) => {
        const prioridad = { Apartado: 0, Pendiente: 1 };
        return prioridad[a.status] - prioridad[b.status];
      });
      setReservas(ordenadas);
      setLoadingPendientes(false);
    } catch (err) {
      setError("Error al obtener las reservas.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReservasCompartidas = async () => {
    setLoadingCompartidas(true);
    setErrorCompartidas(null);
    try {
      const [activas, pendientes] = await Promise.all([
        axios.get(`https://desarrollotecnologicoar.com/api5/reservas_compartidas/${user.email}/Activo`),
        axios.get(`https://desarrollotecnologicoar.com/api5/reservas_compartidas/${user.email}/Pendiente`),
      ]);
      setReservasCompartidas([...activas.data, ...pendientes.data]);
    } catch (err) {
      console.error("Error al obtener compartidas:", err);
      setErrorCompartidas("No se pudieron cargar las reservas compartidas.");
    } finally {
      setLoadingCompartidas(false);
    }
  };

  const fetchReservasActivas = async () => {
    setLoadingActivas(true);
    setSearchTicket("");
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api5/reservas_agente/${user.email}/Activo`);
      setReservasActivas(response.data);
    } catch (error) {
      console.error("Error al obtener reservas activas:", error);
    }
    setLoadingActivas(false);
  };

  const fetchReservasPendientes = async () => {
    setLoadingPendientes(true);
    try {
      const [pendientes, apartadas] = await Promise.all([
        axios.get(`https://desarrollotecnologicoar.com/api5/reservas_agente/${user.email}/Pendiente`),
        axios.get(`https://desarrollotecnologicoar.com/api5/reservas_agente/${user.email}/Apartado`),
      ]);
      const todas = [...pendientes.data, ...apartadas.data];
      setReservas(todas.sort((a, b) => ({ Apartado: 0, Pendiente: 1 }[a.status] - { Apartado: 0, Pendiente: 1 }[b.status])));
    } catch (error) {
      console.error("Error al obtener reservas pendientes y apartadas:", error);
    }
    setLoadingPendientes(false);
  };

  const fetchTecnicos = async () => {
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api5/tecnicos`);
      setTecnicos(response.data);
    } catch (err) {
      console.error("Error al obtener los tecnicos:", err);
    }
  };

  const fetchAgentes = async () => {
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api5/agentes_servicio`);
      setAgente(response.data);
    } catch (err) {
      console.error("Error al obtener los agentes:", err);
    }
  };

  useEffect(() => {
    fetchReservas();
    fetchTecnicos();
    fetchAgentes();
    fetchReservasCompartidas();
  }, []);

  /* --- Handlers --- */
  const handleOpenCompartir = (reserva) => { setSelectedReservaCompartir(reserva); setOpenCompartir(true); };
  const handleCloseCompartir = () => { setOpenCompartir(false); setShareWith(""); };

  const handleShare = async () => {
    try {
      console.log("Compartiendo reserva", selectedReservaCompartir.id, "con", shareWith);
      await axios.put(`https://desarrollotecnologicoar.com/api5/compartir_reserva/${selectedReservaCompartir.id}`, { correo_compartido: shareWith });
      handleCloseCompartir();
      fetchReservas();
    } catch (error) {
      console.error("Error al compartir reserva:", error);
    }
  };

  const handleOpenDrawer = (reserva) => { setSelectedReserva(reserva); setDrawerOpen(true); };
  const handleCloseDrawer = () => setDrawerOpen(false);
  const handleEditar = (idSelect, method) => navigate(`/editar_solicitud_sin_asignar/${idSelect}/${method}`);

  const filteredReservasActivas = reservasActivas.filter((r) => r.ticket.toString().includes(searchTicket));

  const handlePiezas = (link) => { window.location.href = link; };

  const handleEliminarApartado = async (reserva) => {
    const confirmar = window.confirm(
      `¿Eliminar la reserva de "${reserva.empresa}" (${reserva.ciudad})?\nEsto borrará la reserva y sus itinerarios. Esta acción no se puede deshacer.`
    );
    if (!confirmar) return;
    try {
      await axios.delete(`https://desarrollotecnologicoar.com/api5/reservas_apartado_sin_ticket/${reserva.id}`);
      fetchReservasPendientes();
    } catch (err) {
      console.error("Error al eliminar reserva:", err);
      alert(err.response?.data?.error || "Error al eliminar la reserva.");
    }
  };

  /* --- Render --- */
  return (
    <div className="page-container">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="page-title">Actividad</h1>
          <p className="text-sm text-slate-500 mt-1">Gestiona tus solicitudes pendientes y activas</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { fetchReservasPendientes(); fetchReservasCompartidas(); }} disabled={loadingPendientes || loadingCompartidas} className="btn-secondary">
            <HiOutlineArrowPath className={`w-4 h-4 ${loadingPendientes || loadingCompartidas ? "animate-spin" : ""}`} />
            Recargar
          </button>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="xl:col-span-2 space-y-4">
          {/* Solicitudes Pendientes */}
          <h2 className="section-title mb-3">Solicitudes Pendientes</h2>

          {loadingPendientes ? (
            <div className="flex justify-center py-12">
              <svg className="w-8 h-8 animate-spin text-slate-400" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : error ? (
            <div className="card p-6 text-center text-red-600">{error}</div>
          ) : reservas.length === 0 ? (
            <div className="empty-state card py-16">
              <HiOutlineClipboardDocumentCheck className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500 font-medium">No hay reservas pendientes</p>
              <p className="text-sm text-slate-400 mt-1">Las nuevas solicitudes apareceran aqui</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reservas.map((reserva) => {
                const primer = getPrimerDiaItinerario(reserva.itinerario);
                return (
                  <div key={reserva.id ?? reserva.ticket} className="card-hover overflow-hidden">
                    <div className="flex">
                      {/* Content */}
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="text-base font-bold text-slate-900">Ticket #{reserva.ticket}</p>
                            <p className="text-sm font-semibold text-brand-700 uppercase tracking-wide">{reserva.empresa}</p>
                          </div>
                          <span className={statusBadge(reserva.status)}>{reserva.status}</span>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mt-3">
                          <div>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Contacto</p>
                            <p className="text-sm text-slate-700">{reserva.contacto || "\u2014"}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Telefono</p>
                            <p className="text-sm text-slate-700">{reserva.telefono || "\u2014"}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Maquina</p>
                            <p className="text-sm text-slate-700">{reserva.equipo || "\u2014"}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Ciudad</p>
                            <p className="text-sm text-slate-700">{reserva.ciudad || "\u2014"}</p>
                          </div>
                          {reserva.status === "Apartado" && (
                            <div>
                              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Tecnico apartado</p>
                              <p className="text-sm text-slate-700">
                                {reserva.correo_tecnico?.trim() ? (reserva.nombre_tecnico_asignado || reserva.correo_tecnico) : "Sin asignar"}
                              </p>
                            </div>
                          )}
                          {primer && (
                            <div>
                              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Primer dia</p>
                              <p className="text-sm text-slate-700">
                                {formatFechaCortaMX(primer.fecha)} {primer.entrada?.slice(0, 5)} - {primer.salida?.slice(0, 5)}
                              </p>
                            </div>
                          )}
                          <div>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Dias</p>
                            <p className="text-sm text-slate-700">{reserva.itinerario?.length ?? 0}</p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                          <button onClick={() => handleOpenDrawer(reserva)} className="btn-primary text-xs py-1.5 px-3">
                            <HiOutlineInformationCircle className="w-4 h-4" /> Detalles
                          </button>
                          <button onClick={() => handleEditar(reserva.id, "actividad")} className="btn-secondary text-xs py-1.5 px-3">
                            <HiOutlinePencilSquare className="w-4 h-4" /> Editar
                          </button>
                          {reserva.status === "Apartado" && (
                            reserva.correo_tecnico?.trim() ? (
                              <button onClick={() => navigate(`/completar_apartado/${reserva.id}/actividad`)} className="text-xs py-1.5 px-3 inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-btn font-semibold hover:bg-emerald-700 transition-colors">
                                <HiOutlineCheckCircle className="w-4 h-4" /> Completar
                              </button>
                            ) : (
                              <button
                                onClick={() => alert("No se puede completar la reserva: primero debe asignarse un tecnico.")}
                                title="Se tiene que asignar un tecnico antes de completar"
                                className="text-xs py-1.5 px-3 inline-flex items-center gap-1.5 bg-slate-200 text-slate-500 rounded-btn font-semibold cursor-not-allowed"
                              >
                                <HiOutlineCheckCircle className="w-4 h-4" /> Completar
                              </button>
                            )
                          )}
                          {reserva.actividades?.some((act) => act.toLowerCase() === "garantia") && (
                            <button onClick={() => handlePiezas(`https://garantias-ar.web.app/catalogo/${reserva.id}`)} className="text-xs py-1.5 px-3 inline-flex items-center gap-1.5 bg-purple-600 text-white rounded-btn font-semibold hover:bg-purple-700 transition-colors">
                              <HiOutlinePuzzlePiece className="w-4 h-4" /> Solicitar piezas
                            </button>
                          )}
                          <button onClick={() => handleOpenCompartir(reserva)} className="btn-ghost text-xs py-1.5 px-3">
                            <HiOutlineShare className="w-3.5 h-3.5" /> Compartir
                          </button>
                          {reserva.ticket === "SIN_TICKET" && (
                            <button onClick={() => handleEliminarApartado(reserva)} className="text-xs py-1.5 px-3 inline-flex items-center gap-1.5 bg-red-600 text-white rounded-btn font-semibold hover:bg-red-700 transition-colors">
                              <HiOutlineTrash className="w-4 h-4" /> Eliminar
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Right accent strip */}
                      <div className="hidden sm:flex w-28 bg-slate-900 text-white flex-col items-center justify-center shrink-0">
                        <span className="text-[10px] font-bold uppercase tracking-widest opacity-70">Ticket</span>
                        <span className="text-3xl font-black leading-none mt-1 text-center">{reserva.ticket === "SIN_TICKET" ? "SIN TICKET" : reserva.ticket}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Reservas Compartidas */}
          <div className="mt-10">
            <h2 className="section-title mb-3">Reservas Compartidas</h2>
            {loadingCompartidas ? (
              <div className="flex justify-center py-8">
                <svg className="w-6 h-6 animate-spin text-slate-400" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            ) : errorCompartidas ? (
              <p className="text-red-600 text-sm">{errorCompartidas}</p>
            ) : reservasCompartidas.length === 0 ? (
              <div className="card p-8 text-center">
                <p className="text-slate-400 text-sm">No hay reservas compartidas contigo.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reservasCompartidas.map((res) => (
                  <div key={res.id} className="card-hover p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-base font-bold text-slate-900">
                          {res.equipo ? `Maquina ${res.equipo}` : `Ticket #${res.ticket}`}
                        </p>
                        <p className="text-sm font-semibold text-brand-700 uppercase">{res.empresa}</p>
                      </div>
                      <span className="badge badge-blue">#{res.ticket}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mt-2">
                      <div>
                        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Compartido por</p>
                        <p className="text-slate-700">{res.nombre_agente || "\u2014"}</p>
                      </div>
                      {res.ciudad && (
                        <div>
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Ciudad</p>
                          <p className="text-slate-700">{res.ciudad}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                      <button onClick={() => handleOpenDrawer(res)} className="btn-primary text-xs py-1.5 px-3">
                        <HiOutlineInformationCircle className="w-4 h-4" /> Detalles
                      </button>
                      <button onClick={() => handleEditar(res.id, "compartidas")} className="btn-secondary text-xs py-1.5 px-3">
                        <HiOutlinePencilSquare className="w-4 h-4" /> Extender
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="xl:col-span-1">
          <div className="card p-5 sticky top-6">
            <h3 className="font-semibold text-slate-900 mb-1">Solicitudes Aprobadas</h3>
            <p className="text-xs text-slate-500 mb-4">Tus ultimas solicitudes activas</p>

            <div className="relative mb-3">
              <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por ticket..."
                value={searchTicket}
                onChange={(e) => setSearchTicket(e.target.value)}
                className="input pl-9 py-2 text-sm"
              />
            </div>

            <button onClick={fetchReservasActivas} disabled={loadingActivas} className="btn-primary w-full mb-4 text-sm">
              <HiOutlineArrowPath className={`w-4 h-4 ${loadingActivas ? "animate-spin" : ""}`} />
              Actualizar
            </button>

            {loading || loadingActivas || tecnicos.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Cargando...</p>
            ) : filteredReservasActivas.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No hay solicitudes aprobadas.</p>
            ) : (
              <div className="space-y-3">
                {filteredReservasActivas.slice(0, 5).map((reserva) => {
                  const tec = tecnicos.find((t) => t.correo_tecnico === (reserva.correo_tecnico || "").trim());
                  return (
                    <div key={reserva.id} className="rounded-lg border border-slate-100 p-3 hover:border-slate-200 transition-colors">
                      <div className="flex items-start justify-between mb-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">{reserva.empresa}</p>
                        <span className={`${statusBadge(reserva.status)} text-[10px]`}>{reserva.status}</span>
                      </div>
                      <div className="space-y-0.5 text-xs text-slate-500">
                        <p>Ticket: <span className="text-slate-700 font-medium">{reserva.ticket}</span></p>
                        <p>Maquina: <span className="text-slate-700">{reserva.equipo}</span></p>
                        <p>Tecnico: <span className="text-slate-700">{tec?.nombre_tecnico || "No asignado"}</span></p>
                      </div>
                      <div className="flex gap-1.5 mt-2">
                        <button onClick={() => handleEditar(reserva.id, "actividad")} className="btn-ghost text-xs py-1 px-2">
                          Editar
                        </button>
                        <button onClick={() => handleOpenDrawer(reserva)} className="btn-ghost text-xs py-1 px-2">
                          Detalles
                        </button>
                        <button onClick={() => handleOpenCompartir(reserva)} className="btn-ghost text-xs py-1 px-2">
                          <HiOutlineShare className="w-3.5 h-3.5" /> Compartir
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {openCompartir && (
        <>
          <div className="overlay" onClick={handleCloseCompartir} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="modal w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Compartir reserva</h3>
                <button onClick={handleCloseCompartir} className="btn-icon">
                  <HiOutlineXMark className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Al compartir esta reserva, el usuario podra ver todos los detalles de la misma.
              </p>
              <label className="label">Seleccionar usuario</label>
              <select
                value={shareWith}
                onChange={(e) => setShareWith(e.target.value)}
                className="input mb-4"
              >
                <option value="">-- Selecciona un usuario --</option>
                {agentes.map((ag) => (
                  <option key={ag.correo_agente} value={ag.correo_agente}>
                    {ag.nombre_agente} ({ag.correo_agente})
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-2">
                <button onClick={handleCloseCompartir} className="btn-secondary">Cancelar</button>
                <button onClick={handleShare} disabled={!shareWith} className="btn-primary">Enviar</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Detail Drawer */}
      {drawerOpen && (
        <>
          <div className="overlay" onClick={handleCloseDrawer} />
          <aside className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-modal overflow-y-auto">
            {selectedReserva && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-slate-900">{selectedReserva.empresa}</h2>
                  <button onClick={handleCloseDrawer} className="btn-icon">
                    <HiOutlineXMark className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {[
                    { label: "Cliente", value: selectedReserva.contacto },
                    { label: "Maquina", value: selectedReserva.equipo },
                    { label: "Solicitado por", value: selectedReserva.nombre_agente },
                    { label: "Tecnico", value: tecnicos.find((t) => t.correo_tecnico === (selectedReserva.correo_tecnico || "").trim())?.nombre_tecnico || "No asignado" },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">{item.label}</span>
                      <span className="text-sm font-medium text-slate-800">{item.value}</span>
                    </div>
                  ))}

                  <div className="flex items-start gap-2 py-2 border-b border-slate-100">
                    <HiOutlineMapPin className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500 mb-0.5">Ubicacion</p>
                      <p className="text-sm font-medium text-slate-800">{selectedReserva.domicilio}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 py-2 border-b border-slate-100">
                    <HiOutlineCheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500 mb-0.5">Estatus</p>
                      <span className={statusBadge(selectedReserva.status)}>{selectedReserva.status}</span>
                    </div>
                  </div>
                </div>

                {/* Activities */}
                <div className="mt-6">
                  <h3 className="section-title mb-3">Actividades</h3>
                  {selectedReserva.actividades?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedReserva.actividades.map((act, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
                          <HiOutlineChatBubbleLeft className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="capitalize">{act}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">No hay actividades registradas</p>
                  )}
                </div>

                {/* Itinerary */}
                <div className="mt-6">
                  <h3 className="section-title mb-3">Itinerario</h3>
                  {selectedReserva.itinerario?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedReserva.itinerario.map((it, i) => (
                        <div key={i} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg text-sm">
                          <span className="font-medium text-slate-800">{formatFecha(it.fecha)}</span>
                          <span className="text-slate-500">
                            {formatHora(it.entrada)} -- {formatHora(it.salida)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">No hay itinerario registrado</p>
                  )}
                </div>
              </div>
            )}
          </aside>
        </>
      )}
    </div>
  );
};

export default ActivityPage;
